import { isDefined, type NotificationService, sleep } from '@capsicum/common';
import { AnimatePresence, type AnimationProps, motion, useMotionValue, useMotionValueEvent } from 'framer-motion';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';

import { useAnimations } from '../../../hooks/helpers/useAnimation';

/**
 * Motion is an animated div based on Framer Motion. The notifications (i.e: alerts, toasts) may
 * be passed in as children.
 *
 * @param {() => void} onDismiss The "improved" notification onDismiss function
 * @param {boolean} notDismissable Whether the notification is dismissable. False for persisted notifications.
 * @param {JSX.Element} children A notification component such as alerts or toasts
 * @param {number} timeout The duration to display for timer-based notifications
 * @returns {JSX.Element} This component
 */
export const Motion: FC<
	NotificationService.ComponentNotificationProps &
		Omit<NotificationService.Notification, 'component' | 'priority'> & {
			children?: ReactNode;
		}
> = ({ children, hasAnimatedTransition, notDismissable, onDismiss, timeout }) => {
	const [inView, setInView] = useState<boolean>(true);
	const { SlideOutRight } = useAnimations();

	// Attribute to store how far the user has dragged the toast
	const x = useMotionValue(0);

	const variants: AnimationProps['variants'] = useMemo(
		() => ({
			// State after toast is dismissed
			hidden: {
				opacity: 0,
				transition: {
					duration: 0.5
				},
				x: window.innerWidth,
				y: 0
			},

			// When the toast is in view
			view: {
				opacity: 1,
				x: 0,
				y: 0
			}
		}),
		[]
	);

	// When the user moves the toast to the right, we want to dismiss the toast via inertia animation
	useMotionValueEvent(x, 'change', async latest => {
		if (latest > window.innerWidth) {
			setInView(false);

			if (isDefined(onDismiss)) {
				await sleep(500);
				onDismiss();
			}
		}
	});

	useEffect(() => {
		if (timeout) {
			sleep(timeout - 700).then(() => setInView(false));
		}
	}, [timeout]);

	if (!hasAnimatedTransition) {
		return children;
	}

	return (
		<AnimatePresence>
			{inView && (
				<motion.div
					animate={inView ? 'view' : 'hidden'}
					drag={!notDismissable && inView ? 'x' : undefined}
					dragConstraints={inView ? { left: 0, right: 0 } : {}}
					dragElastic={{
						right: 1
					}}
					dragTransition={{
						bounceDamping: 10,
						bounceStiffness: 300,
						max: 100,
						min: 0,
						power: 1.5,
						timeConstant: 500
					}}
					exit={SlideOutRight}
					initial={SlideOutRight}
					style={{ x }}
					transition={{ duration: 0.5 }}
					variants={variants}
				>
					{children}
				</motion.div>
			)}
		</AnimatePresence>
	);
};
